'use client'

import { ChevronRightIcon } from 'lucide-react'
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import {
  type PointerEvent,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { FilterRowInlineContext } from '@/components/ui/filter-row'
import { WidgetMenuRow } from '@/components/widget/widget-menu-row'
import { ChatContainerContext } from '@/features/order/chat/lib/chat-container-context'
import { useIsMobileLayout } from '@/hooks/use-screen'
import { cn } from '@/lib/utils'

/** How long the pointer must rest on the row before its panel opens on hover. */
const HOVER_REST_MS = 200
/** Movement within this radius (px) still counts as resting. */
const HOVER_REST_SLOP_PX = 4

interface WidgetMenuDropdownItemProps {
  icon: ReactNode
  label: string
  trailing?: ReactNode
  /** Also dismiss an owning menu when the pointer lands outside this nested panel. */
  onPointerDownOutside?: () => void
  /** Observe the nested panel opening/closing. */
  onOpenChange?: (open: boolean) => void
  /** The extracted *Content component for the settings/filter/columns panel. */
  children: ReactNode
}

/**
 * A row inside a widget's "..." menu (WidgetActionsMenu's additionalItems) that
 * opens a nested settings/filter/columns panel. It is a Radix submenu of that
 * menu, so the panel opens as a flyout beside the row — to the right, flipping
 * left when the widget sits against the viewport edge — instead of dropping down
 * over the menu it came from. Being inside the same DropdownMenu.Root also gives
 * FilterRow's own DropdownMenuSub the ancestor it needs.
 *
 * Hover opens only once the pointer rests on the row (hover intent), so sweeping
 * across the menu doesn't flash panels that then cover the rows you're heading
 * for. Click and ArrowRight/Enter still open it immediately.
 */
export function WidgetMenuDropdownItem({
  icon,
  label,
  trailing,
  onPointerDownOutside,
  onOpenChange,
  children,
}: WidgetMenuDropdownItemProps) {
  const chatContainer = useContext(ChatContainerContext)
  const [open, setOpen] = useState(false)

  // Closing the owning "..." menu unmounts this whole item without Radix ever
  // firing onOpenChange(false); report that as a close so observers don't get
  // stuck on "open". (An owner that unmounts too — navigation — never acts on it.)
  const isOpenRef = useRef(false)
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  const applyOpen = useCallback((next: boolean) => {
    if (isOpenRef.current === next) return
    isOpenRef.current = next
    setOpen(next)
    onOpenChangeRef.current?.(next)
  }, [])

  const restTimerRef = useRef<number | null>(null)
  const restAnchorRef = useRef<{ x: number; y: number } | null>(null)
  const cancelRest = useCallback(() => {
    if (restTimerRef.current !== null) window.clearTimeout(restTimerRef.current)
    restTimerRef.current = null
    restAnchorRef.current = null
  }, [])

  useEffect(
    () => () => {
      cancelRest()
      if (isOpenRef.current) onOpenChangeRef.current?.(false)
    },
    [cancelRest]
  )

  // Radix opens a submenu 100ms after any pointermove on its trigger. Only honour
  // opens that come from a click or key press; hover opens are driven by the rest
  // timer below instead.
  const explicitOpenRef = useRef(false)
  const markExplicit = () => {
    explicitOpenRef.current = true
    queueMicrotask(() => {
      explicitOpenRef.current = false
    })
  }
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        cancelRest()
        applyOpen(false)
      } else if (explicitOpenRef.current) {
        cancelRest()
        applyOpen(true)
      }
    },
    [applyOpen, cancelRest]
  )

  const handlePointerMove = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse' || isOpenRef.current) return
    const anchor = restAnchorRef.current
    if (
      anchor &&
      Math.abs(event.clientX - anchor.x) <= HOVER_REST_SLOP_PX &&
      Math.abs(event.clientY - anchor.y) <= HOVER_REST_SLOP_PX
    ) {
      return
    }
    cancelRest()
    const trigger = event.currentTarget as HTMLElement
    restAnchorRef.current = { x: event.clientX, y: event.clientY }
    restTimerRef.current = window.setTimeout(() => {
      restTimerRef.current = null
      restAnchorRef.current = null
      // Focusing the row first closes a sibling panel that's still open (Radix
      // closes a submenu when focus leaves it), so only one panel shows at a time.
      trigger.focus()
      applyOpen(true)
    }, HOVER_REST_MS)
  }

  // The panel's outside-click closes the whole owning menu, but its own row (and the rest of
  // that menu) sit outside the panel too: clicking "Group" with its panel open used to close
  // everything. Only a click outside every menu layer dismisses; a click on the row itself
  // keeps the panel open.
  const triggerRef = useRef<HTMLDivElement>(null)
  const handlePointerDownOutside = (event: Event) => {
    const target = event.target instanceof Element ? event.target : null
    if (target && triggerRef.current?.contains(target)) {
      event.preventDefault()
      return
    }
    if (target?.closest('[role="menu"]')) return
    onPointerDownOutside?.()
  }

  // Phone width: a flyout beside the row runs off-screen, and every FilterRow inside it opens
  // another flyout further out. Expand the panel in place below the row instead, with its
  // FilterRows as accordions (FilterRowInlineContext), so the whole menu stays one column.
  const isMobileLayout = useIsMobileLayout()
  if (isMobileLayout) {
    return (
      <div className="flex flex-col">
        <DropdownMenuPrimitive.Item
          asChild
          // Tapping the row toggles the panel; it must not close the owning menu.
          onSelect={(event) => {
            event.preventDefault()
            applyOpen(!isOpenRef.current)
          }}
          aria-expanded={open}
          className={cn(
            'data-[highlighted]:bg-foreground/10 outline-none',
            open && 'bg-foreground/10'
          )}
        >
          <WidgetMenuRow
            icon={icon}
            label={label}
            trailing={
              trailing ?? (
                <ChevronRightIcon
                  aria-hidden="true"
                  className={cn(
                    'text-foreground/40 size-3.5 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
                    open && 'rotate-90'
                  )}
                />
              )
            }
          />
        </DropdownMenuPrimitive.Item>
        {open && (
          <div className="border-foreground/10 max-h-[min(60dvh,480px)] overflow-y-auto border-y">
            <FilterRowInlineContext.Provider value={true}>
              {children}
            </FilterRowInlineContext.Provider>
          </div>
        )}
      </div>
    )
  }

  return (
    <DropdownMenuPrimitive.Sub open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuPrimitive.SubTrigger
        ref={triggerRef}
        asChild
        onClick={markExplicit}
        onKeyDown={markExplicit}
        onPointerMove={handlePointerMove}
        onPointerLeave={cancelRest}
        className="data-[highlighted]:bg-foreground/10 data-[state=open]:bg-foreground/10 outline-none"
      >
        <WidgetMenuRow icon={icon} label={label} trailing={trailing} />
      </DropdownMenuPrimitive.SubTrigger>
      <DropdownMenuPrimitive.Portal container={chatContainer?.ownerDocument?.body}>
        <DropdownMenuPrimitive.SubContent
          align="start"
          sideOffset={2}
          alignOffset={-4}
          collisionPadding={8}
          onPointerDownOutside={handlePointerDownOutside}
          className="menu-surface no-drag data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 z-[60] max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-44 origin-[--radix-dropdown-menu-content-transform-origin] overflow-x-hidden overflow-y-auto p-0"
        >
          {children}
        </DropdownMenuPrimitive.SubContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Sub>
  )
}
